import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { ActionEnum } from "notifications/notification-action";
import NotificationMessage from "notifications/messages/notification-message";
import NotificationRecipient from "notifications/recipients/notification-recipient";
import NotificationRecipientSchema from "notifications/recipients/notification-recipient-schema";
import NotificationTopic from "notifications/topics/notification-topic";
import NotificationTopicSchema from "notifications/topics/notification-topic-schema";
import RecipientName from "notifications/recipients/recipient-name";
import SenderName from "notifications/senders/sender-name";
import { z } from "zod";

export type NotificationJSON = z.infer<typeof NotificationSchema.NotificationJSONZ>;
export type NotificationParams = z.infer<typeof NotificationSchema.NotificationZ>;

/**
 * `actionRequired` names an affordance the recipient is being offered. The set of names lives in the
 * API, which deploys to ECS independently of this bundle on CloudFront — and users keep whatever
 * bundle they cached besides. A closed enum therefore makes the API's notification vocabulary
 * hostage to the oldest bundle in the field: one unrecognized name fails the whole object, the Ably
 * middleware drops the payload, and the list path degrades the row to "Issue loading this
 * notification." The recipient loses a notification whose message, subheader, and body were
 * perfectly readable.
 *
 * So an unrecognized action degrades to "no particular action" rather than rejecting the
 * notification. The notification still arrives and still reads; only the action-specific affordances
 * fall back to their generic defaults — `NotificationLink` routes to a plain view, and the icon
 * lookups render their default icon. The fallback is reported through the allowlisted reporter,
 * because a silently dropped action is exactly how `ClientTeamLeaderReview` reached production
 * unnoticed.
 */
const degradeUnrecognizedAction =
  (shape: "JSON" | "domain") =>
  ({ value }: { value: unknown }): undefined => {
    logSchemaFallback({
      schemaName: "NotificationSchema",
      field: `actionRequired (${shape})`,
      value,
      fallback: undefined,
    });
    return undefined;
  };

/**
 * A notification that has never been aggregated stands for exactly one event (#830).
 *
 * Every notification the API has ever sent before the occurrence count existed, and every
 * notification of a category that is never aggregated, means one occurrence. So does a payload whose
 * count this bundle cannot read.
 */
export const DEFAULT_OCCURRENCE_COUNT = 1;

/**
 * The occurrence count is a number the API aggregates upward while a notification stays unread
 * (#830) — "3 Messages from John Doe" instead of three separate rows. It is a display refinement,
 * not the notification's substance: the message, the sender, and the link all read perfectly well
 * without it.
 *
 * So a count this bundle cannot make sense of — a string from a payload shape that changed, a zero,
 * a negative, a fractional number — degrades to {@link DEFAULT_OCCURRENCE_COUNT} rather than failing
 * the object. Failing it would cost the recipient the whole notification: the Ably middleware drops
 * an unparseable payload, and the list path degrades the row to "Issue loading this notification."
 * An inbox must not be stranded over a badge number.
 *
 * As with `actionRequired`, the fallback is reported through the allowlisted reporter, because a
 * count this bundle silently rewrites is a sign it is behind the API.
 */
const degradeUnreadableOccurrenceCount =
  (shape: "JSON" | "domain") =>
  ({ value }: { value: unknown }): number => {
    logSchemaFallback({
      schemaName: "NotificationSchema",
      field: `occurrenceCount (${shape})`,
      value,
      fallback: DEFAULT_OCCURRENCE_COUNT,
    });
    return DEFAULT_OCCURRENCE_COUNT;
  };

export default class NotificationSchema {
  public static readonly NotificationJSONZ = z.object({
    id: z.uuid(),
    recipients: z.array(NotificationRecipientSchema.NotificationRecipientJSONZ),
    senderId: z.uuid().nullish(),
    message: z.string().nullish(),
    occurrenceCount: z.number().int().positive().nullish().catch(degradeUnreadableOccurrenceCount("JSON")),
    actionRequired: z.enum(ActionEnum).nullish().catch(degradeUnrecognizedAction("JSON")),
    actionLink: z.unknown().optional(),
    createdDate: z.iso.datetime({ offset: true }),
    isCanceled: z.boolean().nullish(),
    senderName: z.string().nullish(),
    recipientName: z.string().nullish(),
    topicCategory: z.string().nullish(),
    topicId: z.uuid().nullish(),
    subheader: z.string().nullish(),
    body: z.string().nullish(),
    finePrint: z.string().nullish(),
    isArchived: z.boolean().nullish(),
  });

  public static readonly NotificationZ = z.object({
    id: z.instanceof(Guid),
    recipients: z.array(z.instanceof(NotificationRecipient)),
    senderUserId: z.instanceof(Guid).optional(),
    message: z.instanceof(NotificationMessage).optional(),
    occurrenceCount: z.number().int().positive().optional().catch(degradeUnreadableOccurrenceCount("domain")),
    topic: z.instanceof(NotificationTopic),
    actionRequired: z.enum(ActionEnum).optional().catch(degradeUnrecognizedAction("domain")),
    actionLink: z.unknown().optional(),
    createdDate: z.instanceof(AHDate),
    isCanceled: z.boolean().optional(),
    senderName: z.instanceof(SenderName).optional(),
    recipientName: z.instanceof(RecipientName).optional(),
    subheader: z.string().optional(),
    body: z.string().optional(),
    finePrint: z.string().optional(),
    isArchived: z.boolean().optional(),
  });

  public static validate(values: unknown): NotificationParams {
    return parseOrThrow(NotificationSchema.NotificationZ, values, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): NotificationJSON {
    return parseOrThrow(NotificationSchema.NotificationJSONZ, values, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is NotificationParams {
    try {
      NotificationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is NotificationJSON {
    try {
      NotificationSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): NotificationParams {
    const parsed = parseOrThrow(NotificationSchema.NotificationJSONZ, data, {
      logError: true,
      schemaName: "NotificationSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationSchema.codec.decode(parsed);
  }

  public static serialize(notification: NotificationParams | null | undefined): NotificationJSON | null | undefined {
    if (isNullOrUndefined(notification)) return notification;
    const parsed = NotificationSchema.validate(notification);
    if (isNullOrUndefined(parsed)) return parsed;
    return NotificationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(NotificationSchema.NotificationJSONZ, NotificationSchema.NotificationZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      recipients: data.recipients.map((r) => new NotificationRecipient(NotificationRecipientSchema.deserialize(r))),
      senderUserId: Guid.fromJSON(data.senderId) ?? undefined,
      message: NotificationMessage.fromJSON(data.message) ?? undefined,
      occurrenceCount: data.occurrenceCount ?? DEFAULT_OCCURRENCE_COUNT,
      topic: new NotificationTopic(
        NotificationTopicSchema.deserialize({ topicId: data.topicId, topicCategory: data.topicCategory })
      ),
      actionRequired: data.actionRequired ?? undefined,
      actionLink: data.actionLink,
      createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
      isCanceled: data.isCanceled ?? undefined,
      senderName: SenderName.fromJSON(data.senderName) ?? undefined,
      recipientName: RecipientName.fromJSON(data.recipientName) ?? undefined,
      subheader: data.subheader ?? undefined,
      body: data.body ?? undefined,
      finePrint: data.finePrint ?? undefined,
      isArchived: data.isArchived ?? undefined,
    }),
    encode: (notification) => ({
      id: notification.id.value,
      recipients: notification.recipients.map((r) => ({
        userId: r.userId.value,
        seenAt: r.seenAt?.toISOString() ?? null,
      })),
      senderId: notification.senderUserId?.toJSON(),
      message: notification.message?.value,
      occurrenceCount: notification.occurrenceCount ?? DEFAULT_OCCURRENCE_COUNT,
      topicId: notification.topic?.id?.toJSON(),
      topicCategory: notification.topic?.category,
      actionRequired: notification.actionRequired ?? undefined,
      actionLink: notification.actionLink,
      createdDate: notification.createdDate?.toISOString() ?? "",
      isCanceled: notification.isCanceled,
      senderName: notification.senderName?.toJSON(),
      recipientName: notification.recipientName?.toJSON(),
      subheader: notification.subheader,
      body: notification.body,
      finePrint: notification.finePrint,
      isArchived: notification.isArchived,
    }),
  });
}
